// The Ledger (L): a brass-and-paper document, not a skill sheet. Known
// For, then the made things — yours and the ones in your hands — then
// qualitative proof cards. Never a number, never a bar (the spec's hard
// rule).
//
// Crafting v2 makes this the holder's surface too: everything you hold
// carries its maker's mark read aloud (the legend), and the verbs a thing
// affords — give it, wear it, drink it, play it, throw it away — live on
// its card. Give goes through the same trade rails as everything else:
// somebody within reach must take it from your hands.

import { type ArtifactVerb, type ArtifactView, areaLabel } from "@bebop/shared";
import { flyerCard } from "./bench";
import { escapeHtml } from "./escape";

interface CommissionView {
  id: string;
  bookingId: string;
  clubId: string;
  nightTitle: string;
  requestedByName: string;
  note: string;
}

interface OpenedCommissionView {
  id: string;
  clubId: string;
  nightTitle: string;
  status: string;
  crafterName: string;
  filledArtifactId: string | null;
}

interface ArtifactsResponse {
  made: ArtifactView[];
  held: ArtifactView[];
  claimable: ArtifactView[];
  commissions: CommissionView[];
  commissionsOpened: OpenedCommissionView[];
}

interface ProfileResponse {
  paths: Array<{ id: string; publicName: string; sectorName: string }>;
  knownFor: string[];
  capabilities: Array<{ id: string; pathId: string; publicName: string; status: string }>;
}

interface ProofCardsResponse {
  cards: Array<{ pathId: string; title: string; body: string; section?: string }>;
}

/** How the world talks to the room on this panel's behalf. */
export interface LedgerBridge {
  peopleWithinReach(): Array<{ sessionId: string; name: string }>;
  offerArtifact(toSessionId: string, artifactId: string): void;
  useArtifact(artifactId: string, verb: ArtifactVerb): void;
}

/** An ask's fate, in the asker's voice. */
function askStatusWord(status: string): string {
  if (status === "open") return "waiting on them";
  if (status === "filled") return "answered — the flyer is printed";
  if (status === "declined") return "they passed on it";
  if (status === "withdrawn") return "you took it back";
  return status;
}

export class LedgerPanel {
  private root: HTMLDivElement;
  private body: HTMLDivElement;
  private open = false;

  constructor(
    parent: HTMLElement,
    private bridge?: LedgerBridge,
  ) {
    this.root = document.createElement("div");
    this.root.className = "panel-float ledger-panel";
    this.root.innerHTML = `
      <div class="panel-title">THE LEDGER <span class="panel-hint">L to close</span></div>
      <div class="ledger-body"></div>
    `;
    this.root.style.display = "none";
    parent.append(this.root);
    this.body = this.root.querySelector(".ledger-body") as HTMLDivElement;
  }

  toggle(): void {
    this.open = !this.open;
    this.root.style.display = this.open ? "" : "none";
    if (this.open) void this.refresh();
  }

  /** Repaint only if somebody is looking at it. */
  async refreshIfOpen(): Promise<void> {
    if (this.open) await this.refresh();
  }

  private async refresh(): Promise<void> {
    this.body.innerHTML = `<div class="panel-empty">opening the ledger…</div>`;
    try {
      const [profileRes, cardsRes, artifactsRes] = await Promise.all([
        fetch("/api/professions/profile"),
        fetch("/api/professions/proof-cards"),
        fetch("/api/artifacts"),
      ]);
      if (!profileRes.ok || !cardsRes.ok) throw new Error("fetch failed");
      const profile = (await profileRes.json()) as ProfileResponse;
      const cards = (await cardsRes.json()) as ProofCardsResponse;
      // The Ledger still reads without the workshop half.
      const artifacts = artifactsRes.ok
        ? ((await artifactsRes.json()) as ArtifactsResponse)
        : { made: [], held: [], claimable: [], commissions: [], commissionsOpened: [] };
      this.render(profile, cards, artifacts);
    } catch {
      this.body.innerHTML = `<div class="panel-empty">the ink hasn't dried (try again)</div>`;
    }
  }

  /** The card's second line: night for a flyer, quality words otherwise. */
  private cardLine(a: ArtifactView): string {
    if (a.artifactType === "flyer") {
      return `${a.nightTitle || "an unnamed night"} · ${areaLabel(a.clubId)}`;
    }
    const words = a.aspects.map((x) => `${x.label} is ${x.word}`).join(" · ");
    return words || a.legend;
  }

  /** One artifact, with its legend and whatever verbs it affords. */
  private artifactCard(a: ArtifactView, footer: string): string {
    const verbs: string[] = [];
    if (a.owned && !a.consumed) {
      verbs.push(
        `<button class="ledger-verb" data-verb="give" data-artifact="${escapeHtml(a.id)}">give</button>`,
      );
      if (a.artifactType === "drink") {
        verbs.push(
          `<button class="ledger-verb" data-verb="drink" data-artifact="${escapeHtml(a.id)}">drink</button>`,
        );
      }
      if (a.artifactType === "instrument") {
        verbs.push(
          `<button class="ledger-verb" data-verb="play" data-artifact="${escapeHtml(a.id)}">play</button>`,
        );
      }
      if (a.artifactType === "garment") {
        verbs.push(
          a.equipped
            ? `<button class="ledger-verb" data-verb="shed" data-artifact="${escapeHtml(a.id)}">take off</button>`
            : `<button class="ledger-verb" data-verb="wear" data-artifact="${escapeHtml(a.id)}">wear</button>`,
        );
      }
    }
    if (a.owned || a.keepsake) {
      verbs.push(
        `<button class="ledger-verb ledger-discard" data-verb="discard" data-artifact="${escapeHtml(a.id)}">throw away</button>`,
      );
    }
    const state = a.consumed
      ? `<span class="ledger-state">drunk, and remembered</span>`
      : a.equipped
        ? `<span class="ledger-state">worn</span>`
        : "";
    return `
      <div class="ledger-artifact" data-artifact="${escapeHtml(a.id)}">
        ${flyerCard(a.look, a.title, this.cardLine(a), footer)}
        <div class="ledger-legend">${escapeHtml(a.legend)}</div>
        <div class="ledger-standing" data-artifact="${escapeHtml(a.id)}"></div>
        <div class="ledger-verbs">
          <button class="ledger-verb" data-verb="inspect" data-artifact="${escapeHtml(a.id)}">read the mark</button>
          ${verbs.join("")}
          ${state}
        </div>
      </div>`;
  }

  private render(
    profile: ProfileResponse,
    cards: ProofCardsResponse,
    artifacts: ArtifactsResponse,
  ): void {
    const pathName = (id: string) => profile.paths.find((p) => p.id === id)?.publicName ?? id;

    const knownFor =
      profile.knownFor.length > 0
        ? profile.knownFor.map((k) => `<div class="ledger-known">${escapeHtml(k)}</div>`).join("")
        : `<div class="ledger-known faint">Nothing yet. The Ledger fills itself.</div>`;

    const held = profile.capabilities
      .filter((c) => c.status === "active")
      .map(
        (c) =>
          `<span class="ledger-cap">${escapeHtml(c.publicName)}<em>${escapeHtml(pathName(c.pathId))}</em></span>`,
      )
      .join("");

    // The server orders cards: standing, then practices, then open doors
    // (the professions doc's Calling-first frame). Each non-empty band
    // gets its own worded header rather than one crowded pile.
    const cardHtml = (section: string) =>
      cards.cards
        .filter((card) => (card.section ?? "practice") === section)
        .map(
          (card) => `
        <div class="ledger-card">
          <div class="ledger-card-path">${escapeHtml(pathName(card.pathId))}</div>
          <div class="ledger-card-title">${escapeHtml(card.title)}</div>
          <div class="ledger-card-body">${escapeHtml(card.body)}</div>
        </div>`,
        )
        .join("");
    const standingCards = cardHtml("standing");
    const practiceCards = cardHtml("practice");
    const openCards = cardHtml("open");

    const asked = artifacts.commissions
      .map(
        (c) => `
        <div class="ledger-ask">
          <span class="ledger-ask-who">${escapeHtml(c.requestedByName)}</span> asked you to print
          ${escapeHtml(c.nightTitle) || "a night"} at ${escapeHtml(areaLabel(c.clubId))}
          <button class="ledger-decline" data-commission="${escapeHtml(c.id)}">pass on it</button>
        </div>`,
      )
      .join("");

    // The Host's side of the counter: what you asked for, and how each ask
    // went. An open one can be taken back; a filled one names the printer.
    const yourAsks = artifacts.commissionsOpened
      .map(
        (c) => `
        <div class="ledger-ask">
          You asked <span class="ledger-ask-who">${escapeHtml(c.crafterName)}</span> to print
          ${escapeHtml(c.nightTitle) || "a night"} at ${escapeHtml(areaLabel(c.clubId))}
          — ${escapeHtml(askStatusWord(c.status))}
          ${c.status === "open" ? `<button class="ledger-withdraw" data-commission="${escapeHtml(c.id)}">take it back</button>` : ""}
        </div>`,
      )
      .join("");

    const claimable = artifacts.claimable
      .map(
        (a) => `
        <div class="ledger-claimable">
          ${flyerCard(a.look, a.title, this.cardLine(a), `printed by ${a.makerName}`)}
          <button class="ledger-claim" data-artifact="${escapeHtml(a.id)}">take one</button>
        </div>`,
      )
      .join("");

    // What outlives the moment. A made thing here is not a stat line: it
    // is the object, painted from the ground it was made of, with a name
    // on it — and, for the maker, a line about whose hands it is in now.
    const made = artifacts.made
      .map((a) =>
        this.artifactCard(
          a,
          a.consumed
            ? "your mark — drunk, as intended"
            : a.owned
              ? "your mark"
              : `your mark — in ${a.holderName}'s hands`,
        ),
      )
      .join("");
    const heldThings = artifacts.held
      .map((a) => this.artifactCard(a, `made by ${a.makerName}`))
      .join("");

    // The page reads Calling first (what you are known for and hold),
    // then the practices, then the recognitions — asks made of you,
    // nights you were at, work of yours in other hands.
    this.body.innerHTML = `
      <div class="ledger-section">KNOWN FOR</div>
      ${knownFor}
      <div class="ledger-section">IN HAND</div>
      <div class="ledger-caps">${held || '<span class="faint">—</span>'}</div>
      ${standingCards ? `<div class="ledger-section">YOUR STANDING</div>${standingCards}` : ""}
      ${practiceCards ? `<div class="ledger-section">THE PRACTICE</div>${practiceCards}` : ""}
      ${openCards ? `<div class="ledger-section">OPEN DOORS</div>${openCards}` : ""}
      ${asked ? `<div class="ledger-section">ASKED OF YOU</div>${asked}` : ""}
      ${yourAsks ? `<div class="ledger-section">YOUR ASKS</div>${yourAsks}` : ""}
      ${claimable ? `<div class="ledger-section">YOU WERE THERE</div>${claimable}` : ""}
      ${made ? `<div class="ledger-section">THINGS YOU MADE</div>${made}` : ""}
      ${heldThings ? `<div class="ledger-section">IN YOUR KEEPING</div>${heldThings}` : ""}
    `;

    for (const button of this.body.querySelectorAll<HTMLButtonElement>(".ledger-claim")) {
      button.addEventListener("click", () => void this.claim(button));
    }
    for (const button of this.body.querySelectorAll<HTMLButtonElement>(".ledger-decline")) {
      button.addEventListener("click", () => void this.answerAsk(button, "decline"));
    }
    for (const button of this.body.querySelectorAll<HTMLButtonElement>(".ledger-withdraw")) {
      button.addEventListener("click", () => void this.answerAsk(button, "withdraw"));
    }
    for (const button of this.body.querySelectorAll<HTMLButtonElement>(".ledger-verb")) {
      button.addEventListener("click", () => void this.artifactVerb(button));
    }
  }

  /** Take a copy of a flyer for a night you were actually at. */
  private async claim(button: HTMLButtonElement): Promise<void> {
    const id = button.dataset.artifact;
    if (!id) return;
    button.disabled = true;
    button.textContent = "taking…";
    try {
      const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}/claim`, { method: "POST" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        button.textContent = body.error ?? "it stayed on the pile";
        return;
      }
      await this.refresh();
    } catch {
      button.textContent = "it stayed on the pile";
    }
  }

  /** Decline an ask made of you, or take back one you made. */
  private async answerAsk(button: HTMLButtonElement, verb: "decline" | "withdraw"): Promise<void> {
    const id = button.dataset.commission;
    if (!id) return;
    button.disabled = true;
    try {
      const res = await fetch(`/api/commissions/${encodeURIComponent(id)}/${verb}`, {
        method: "POST",
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        button.textContent = body.error ?? "that did not take";
        return;
      }
      await this.refresh();
    } catch {
      button.textContent = "that did not take";
    }
  }

  /** A verb on a made thing: read, give, wear, drink, play, discard. */
  private async artifactVerb(button: HTMLButtonElement): Promise<void> {
    const id = button.dataset.artifact;
    const verb = button.dataset.verb;
    if (!id || !verb) return;

    if (verb === "inspect") {
      const standing = this.body.querySelector<HTMLDivElement>(
        `.ledger-standing[data-artifact="${CSS.escape(id)}"]`,
      );
      if (!standing) return;
      try {
        const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}`);
        if (!res.ok) return;
        const body = (await res.json()) as { makerStanding?: string };
        standing.textContent =
          body.makerStanding || "The mark is fresh; the world has no opinion yet.";
      } catch {
        // The legend is already on the card; standing is a bonus.
      }
      return;
    }

    if (verb === "discard") {
      button.disabled = true;
      try {
        const res = await fetch(`/api/artifacts/${encodeURIComponent(id)}/discard`, {
          method: "POST",
        });
        if (res.ok) await this.refresh();
        else button.textContent = "it stays";
      } catch {
        button.textContent = "it stays";
      }
      return;
    }

    if (verb === "give") {
      this.showGivePicker(button, id);
      return;
    }

    if (verb === "drink" || verb === "play" || verb === "wear" || verb === "shed") {
      this.bridge?.useArtifact(id, verb);
      // The room answers over the wire; refresh shortly so worn/consumed
      // states catch up without waiting for a reopen.
      window.setTimeout(() => void this.refreshIfOpen(), 600);
    }
  }

  /** Hand a made thing to somebody standing with you. */
  private showGivePicker(button: HTMLButtonElement, artifactId: string): void {
    const row = button.closest(".ledger-artifact");
    if (!row) return;
    row.querySelector(".give-picker")?.remove();
    const people = this.bridge?.peopleWithinReach() ?? [];
    const picker = document.createElement("div");
    picker.className = "give-picker";
    if (people.length === 0) {
      picker.innerHTML = `<span class="panel-empty">nobody within reach</span>`;
      row.append(picker);
      window.setTimeout(() => picker.remove(), 2500);
      return;
    }
    for (const person of people) {
      const target = document.createElement("button");
      target.className = "give-target";
      target.textContent = person.name;
      target.addEventListener("click", () => {
        this.bridge?.offerArtifact(person.sessionId, artifactId);
        picker.remove();
      });
      picker.append(target);
    }
    row.append(picker);
  }
}
